import { api } from "./api";
import { loadCore } from "./wasm";

// Thumbnails for the queue. A list of a thousand names tells you nothing about
// what the builds are; a picture next to each one lets you skim them.
//
// Rendered in the BROWSER, with the WebAssembly core and the block atlas that
// are loaded anyway. The alternative -- rendering server-side -- would mean
// putting a C++ binary in the runtime image and re-rendering per deployment,
// for a picture every client could produce itself in a few milliseconds.
//
// Two caches: one in memory for this session, one in IndexedDB so a reload (or
// tomorrow) does not redo the work. Keyed by structure id AND the size of its
// bytes, so an edited build gets a fresh picture instead of a stale one.

const MEM = new Map<string, string>();
const MEASURED = new Set<number>();
const SIZE = 128;
const DB_NAME = "studio-thumbs";
const STORE = "png";

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE))
          req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      // Private windows and blocked site data: fall back to memory only.
      resolve(null);
    }
  });
  return dbPromise;
}

async function cacheGet(key: string): Promise<string | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE).objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function cachePut(key: string, value: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    db.transaction(STORE, "readwrite").objectStore(STORE).put(value, key);
  } catch {
    /* out of quota: the memory cache still works */
  }
}

// One offscreen canvas for every thumbnail: creating a WebGL context per
// picture would exhaust the browser's context limit within a few rows.
let gl: WebGL2RenderingContext | null = null;
let canvas: HTMLCanvasElement | null = null;
let prog: WebGLProgram | null = null;
let vbo: WebGLBuffer | null = null;
let vao: WebGLVertexArrayObject | null = null;
let tex: WebGLTexture | null = null;
let atlasSize: [number, number] = [1, 1];

const VERT = `#version 300 es
layout(location=0) in vec3 a_pos;
layout(location=1) in vec2 a_uv;
layout(location=2) in vec3 a_col;
layout(location=3) in float a_tex;
uniform mat4 u_mvp;
uniform vec2 u_atlas_size;
out vec2 v_uv; out vec3 v_col; out float v_tex;
void main(){ v_uv=a_uv/max(u_atlas_size,vec2(1.0)); v_col=a_col; v_tex=a_tex;
  gl_Position=u_mvp*vec4(a_pos,1.0); }`;

const FRAG = `#version 300 es
precision highp float;
in vec2 v_uv; in vec3 v_col; in float v_tex;
uniform sampler2D u_tex;
out vec4 frag;
void main(){ vec4 c=vec4(v_col,1.0);
  if(v_tex>0.5){ vec4 t=texture(u_tex,v_uv); if(t.a<0.1) discard; c=vec4(t.rgb*v_col,t.a); }
  frag=c; }`;

function ensureGl(): boolean {
  if (gl) return true;
  canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("webgl2", {
    antialias: true,
    preserveDrawingBuffer: true,
  });
  if (!ctx) return false;
  gl = ctx;
  const sh = (type: number, src: string) => {
    const s = gl!.createShader(type)!;
    gl!.shaderSource(s, src);
    gl!.compileShader(s);
    return s;
  };
  prog = gl.createProgram()!;
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  vao = gl.createVertexArray();
  vbo = gl.createBuffer();
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  const st = 9 * 4;
  gl.vertexAttribPointer(0, 3, gl.FLOAT, false, st, 0);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, st, 12);
  gl.vertexAttribPointer(2, 3, gl.FLOAT, false, st, 20);
  gl.vertexAttribPointer(3, 1, gl.FLOAT, false, st, 32);
  for (let i = 0; i < 4; ++i) gl.enableVertexAttribArray(i);
  gl.enable(gl.DEPTH_TEST);
  return true;
}

function mvpFor(sx: number, sy: number, sz: number): Float32Array {
  // Fixed three-quarter view, framed on the build -- the same angle the main
  // viewer opens with, so a thumbnail and the big picture match.
  const span = Math.max(sx, sy, sz);
  const dist = Math.max(6, span * 1.9);
  const yaw = (35 * Math.PI) / 180;
  const pitch = (28 * Math.PI) / 180;
  const t = [sx / 2, sy * 0.4, sz / 2];
  const eye = [
    t[0] + Math.cos(pitch) * Math.sin(yaw) * dist,
    t[1] + Math.sin(pitch) * dist,
    t[2] + Math.cos(pitch) * Math.cos(yaw) * dist,
  ];
  const f = 1 / Math.tan((45 * Math.PI) / 360);
  const near = 0.1;
  const far = 4000;
  const nf = 1 / (near - far);
  const P = [f, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
  const sub = (a: number[], b: number[]) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const norm = (v: number[]) => {
    const l = Math.hypot(...v) || 1;
    return [v[0] / l, v[1] / l, v[2] / l];
  };
  const cross = (a: number[], b: number[]) => [
    a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0],
  ];
  const dot = (a: number[], b: number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const z = norm(sub(eye, t));
  const x = norm(cross([0, 1, 0], z));
  const y = cross(z, x);
  const V = [
    x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0,
    -dot(x, eye), -dot(y, eye), -dot(z, eye), 1,
  ];
  const o = new Float32Array(16);
  for (let c = 0; c < 4; ++c)
    for (let r = 0; r < 4; ++r) {
      let s = 0;
      for (let k = 0; k < 4; ++k) s += P[k * 4 + r] * V[c * 4 + k];
      o[c * 4 + r] = s;
    }
  return o;
}

/**
 * The extents of one build, in blocks, reported to the server on the way past.
 *
 * The server has no .schem parser and should not grow one -- a second NBT
 * implementation next to the C++ core is exactly the kind of duplicate that
 * drifts apart. So the client, which has parsed the build anyway, hands the
 * numbers back. Done once per session per build; never throws.
 */
export async function measure(
  id: number,
): Promise<{ sx: number; sy: number; sz: number } | null> {
  if (MEASURED.has(id)) return null;
  MEASURED.add(id);
  try {
    const core = await loadCore();
    const info = core.loadSchematic(await api.schem(id));
    if ((info as unknown as { error?: string }).error) return null;
    const dims = { sx: info.sx, sy: info.sy, sz: info.sz };
    core.freeSchematic(info.handle);
    await api.reportDims(id, dims.sx, dims.sy, dims.sz);
    return dims;
  } catch {
    return null;
  }
}

/**
 * A data URL for one structure's thumbnail. Never throws: on any failure the
 * caller gets null and shows the plain row it would have shown anyway.
 */
export async function thumbnail(id: number, bytesHint = 0): Promise<string | null> {
  const key = `${id}-${bytesHint}`;
  const mem = MEM.get(key);
  if (mem) return mem;
  const cached = await cacheGet(key);
  if (cached) {
    MEM.set(key, cached);
    return cached;
  }
  try {
    const core = await loadCore();
    if (!ensureGl() || !gl || !prog) return null;

    const bytes = await api.schem(id);
    const info = core.loadSchematic(bytes);
    if ((info as unknown as { error?: string }).error) return null;
    // No ground, no grid, no rings: at this size only the build reads.
    const mesh = core.buildMesh(info.handle, {
      yOffset: 0,
      ground: false,
      footprint: false,
      grid: false,
      markSunken: false,
      showFoundation: false,
      sliceTop: -1,
    });
    core.freeSchematic(info.handle);

    // The build is parsed; the extents cost nothing extra here.
    if (!MEASURED.has(id)) {
      MEASURED.add(id);
      void api.reportDims(id, info.sx, info.sy, info.sz).catch(() => {});
    }

    // Upload the atlas once.
    if (!tex && core.assetsReady()) {
      const atlas = core.atlas();
      if (atlas) {
        tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, atlas.width, atlas.height, 0,
                      gl.RGBA, gl.UNSIGNED_BYTE, atlas.pixels);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        atlasSize = [atlas.width, atlas.height];
      }
    }

    gl.viewport(0, 0, SIZE, SIZE);
    gl.clearColor(0.13, 0.14, 0.16, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.useProgram(prog);
    gl.uniformMatrix4fv(gl.getUniformLocation(prog, "u_mvp"), false,
                        mvpFor(info.sx, info.sy, info.sz));
    gl.uniform2f(gl.getUniformLocation(prog, "u_atlas_size"), atlasSize[0], atlasSize[1]);
    gl.uniform1i(gl.getUniformLocation(prog, "u_tex"), 0);
    gl.activeTexture(gl.TEXTURE0);
    if (tex) gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, mesh.solid, gl.DYNAMIC_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, mesh.solid.length / 9);

    const url = canvas!.toDataURL("image/png");
    MEM.set(key, url);
    void cachePut(key, url);
    return url;
  } catch {
    return null;
  }
}
